import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { removeAccents } from '../../utils/searchUtils';
import { Dropdown } from './Dropdown';

interface DropdownTypeaheadProps<T> {
  value: string;
  onChange: (value: string, item?: T) => void;
  fetchOptions?: (query: string) => Promise<T[]>;
  options?: T[];
  getOptionLabel: (item: T) => string;
  getOptionValue: (item: T) => string | number;
  placeholder?: string;
  className?: string;
  showDropdownIcon?: boolean;
  error?: boolean;
  inputId?: string;
  ariaDescribedBy?: string;
}

export function DropdownTypeahead<T>({
  value,
  onChange,
  fetchOptions,
  options,
  getOptionLabel,
  getOptionValue,
  placeholder,
  className = '',
  showDropdownIcon = true,
  error = false,
  inputId,
  ariaDescribedBy,
}: DropdownTypeaheadProps<T>) {
  const [query, setQuery] = useState(value);
  const [suggestions, setSuggestions] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();

  // Set right before any local edit that also calls `onChange`, since that
  // call can lead the parent to feed back a new `value` prop derived from
  // the edit itself (e.g. clearing the selection while the user types free
  // text). Without this, the resulting prop-sync effect below would stomp
  // the query the user just typed.
  const skipNextValueSyncRef = useRef(false);

  const normalizeOptionLabel = useCallback((label: string) => {
    return removeAccents(label).trim().toLowerCase();
  }, []);

  const selectExactMatch = useCallback(() => {
    const normalizedQuery = normalizeOptionLabel(query);
    if (!normalizedQuery) return false;

    const searchableOptions = options ?? suggestions;
    const match = searchableOptions.find(
      item => normalizeOptionLabel(getOptionLabel(item)) === normalizedQuery
    );

    if (!match) return false;

    const label = getOptionLabel(match);
    skipNextValueSyncRef.current = true;
    setQuery(label);
    onChange(label, match);
    return true;
  }, [getOptionLabel, normalizeOptionLabel, onChange, options, query, suggestions]);

  useEffect(() => {
    if (skipNextValueSyncRef.current) {
      skipNextValueSyncRef.current = false;
      return;
    }
    setQuery(value);
  }, [value]);

  useEffect(() => {
    if (query.length < 2 && !options) {
      setSuggestions([]);
      setIsLoading(false);
      return;
    }

    if (fetchOptions) {
      let active = true;
      const fetchData = async () => {
        setIsLoading(true);
        try {
          const data = await fetchOptions(query);

          if (active) setSuggestions(data);
        } finally {
          if (active) setIsLoading(false);
        }
      };
      fetchData();
      return () => {
        active = false;
      };
    } else if (options && !query) {
      setSuggestions(options);
      setIsLoading(false);
    } else if (options) {
      const filtered = options.filter(item =>
        removeAccents(getOptionLabel(item).toLowerCase()).includes(
          removeAccents(query.toLowerCase())
        )
      );
      setSuggestions(filtered);
      setIsLoading(false);
    }
  }, [query, options, fetchOptions, getOptionLabel]);

  return (
    <Dropdown
      value={query}
      onChange={e => {
        skipNextValueSyncRef.current = true;
        setQuery(e);
        onChange(e, undefined);
      }}
      placeholder={placeholder}
      className={className}
      showDropdownIcon={showDropdownIcon}
      error={error}
      inputId={inputId}
      ariaDescribedBy={ariaDescribedBy}
      onBlur={selectExactMatch}
      onKeyDown={event => {
        if (event.key === 'Enter' && selectExactMatch()) {
          event.preventDefault();
        } else if (event.key === 'Tab') {
          selectExactMatch();
        }
      }}
    >
      {isLoading && (
        <li className="px-4 py-2.5 text-secondary/40 italic font-ambit text-[0.9375rem]">
          {t('loading')}
        </li>
      )}
      {suggestions.map(item => (
        <li
          key={getOptionValue(item)}
          className="px-4 py-2.5 cursor-pointer border-b border-primary/10 last:border-b-0 hover:bg-primary/5 hover:text-primary font-ambit text-[0.9375rem] not-italic font-normal leading-[1.25rem] text-secondary transition-colors"
          onMouseDown={() => {
            skipNextValueSyncRef.current = true;
            setQuery(getOptionLabel(item));
            onChange(getOptionLabel(item), item);
          }}
        >
          {getOptionLabel(item)}
        </li>
      ))}
    </Dropdown>
  );
}
